/*
 * CreateImportDatabaseDialog.tsx — dependency-free version.
 * Uses local ui.tsx primitives + inline icons. Styling uses scoped ih-*
 * classes plus inline values that reference importHub.css variables.
 */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { toast } from './lib/toast';
import * as XLSX from 'xlsx';
import { Dialog, cx } from './ui';
import {
  X,
  Check,
  Workflow,
  List,
  Download,
  Clock,
  ChevronDown,
  AlertCircle,
  Upload,
  FileStack,
  Sparkles,
} from './icons';
import { ImportMappingAPI } from 'api/ImportMappingAPI';
import {
  buildFileProfile,
  buildNormalizedCsvFile,
  describeNormalization,
  LOW_CONFIDENCE_THRESHOLD,
  readWorkbookSheets,
  rewriteCsvFile,
} from './lib/import-mapping';
// Separate type-only import: inline `type` specifiers need TypeScript 4.5+,
// and this project targets 4.3.
import type {
  ColumnHints,
  ColumnMappingPlan,
  MappingSource,
  NormalizationStats,
  WorkbookSheet,
} from './lib/import-mapping';
import {
  createImportDatabase,
  bulkUploadImportProducts,
  waitForImportProductJob,
  type ImportProductJobState,
} from './lib/import-service';
import {
  DEFAULT_IMPORT_DATABASE_ACCENT,
  DEFAULT_IMPORT_DATABASE_ICON,
  getImportDatabaseIcon,
  IMPORT_DATABASE_ACCENTS,
  IMPORT_DATABASE_ICON_CHOICES,
} from './databaseAppearance';
import {
  checkProductCredits,
  type ProductCreditCheck,
} from './lib/product-credits';
import { ProductCreditsModal } from './ProductCreditsModal';

/* ---------- Icon + accent choices ---------- */
const ICON_CHOICES = IMPORT_DATABASE_ICON_CHOICES;
const ACCENTS = IMPORT_DATABASE_ACCENTS;

/* ---------- CSV template ----------
 * Mirrors the legacy Products Database tool's canonical column set
 * (productTemplateColumns, client/src/api/bitedb/products/products.hooks.ts)
 * — these are the literal keys the product service's bulk-import pipeline
 * checks a row's OWN header against (PRODUCT_TEMPLATE_COLUMNS in
 * biteservices product.service.ts) to store as real columns; anything else
 * lands in additional_columns or is dropped. Using any other field set here
 * (as this file previously did) silently fails to populate the real schema.
 */
export const TEMPLATE_COLUMNS = [
  'model_number',
  'parent',
  'name',
  'description',
  'traces',
  'category',
  'hierarchy_type',
  'quantity',
  'unit_of_measure',
  'manufacturer',
  'vendor',
  'specifications',
  'country_of_origin',
  'hts_code',
  'trans_method',
  'country_of_smelt_pour',
  'entry_date',
  'loading_date',
  'product_value',
  'copper_value',
  'aluminum_value',
  'steel_value',
  'copper_percent',
  'aluminum_percent',
  'steel_percent',
] as const;
export type TemplateColumn = (typeof TEMPLATE_COLUMNS)[number];
type ImportDatabaseStructure = 'hierarchy' | 'flat';

const STRUCTURE_TEMPLATE_COLUMNS: Record<
  ImportDatabaseStructure,
  readonly TemplateColumn[]
> = {
  hierarchy: TEMPLATE_COLUMNS,
  flat: TEMPLATE_COLUMNS.filter(
    (column) => column !== 'parent' && column !== 'hierarchy_type',
  ),
};

export const templateColumnsForStructure = (
  databaseStructure?: string | null,
): readonly TemplateColumn[] =>
  STRUCTURE_TEMPLATE_COLUMNS[
    databaseStructure === 'flat' || databaseStructure === 'hts-code-grouping'
      ? 'flat'
      : 'hierarchy'
  ];

const PGA_MASTER_SOURCE_CODES = [
  'FDA',
  'NHT',
  'CPS',
  'DTC',
  'DEA',
  'AMS',
  'APH CORE',
  'APH LACEY',
  'ATF',
  'EPA TSCA',
  'EPA HFC',
  'EPA PST',
  'EPA VNE',
  'FSIS',
  'FWS',
  'NMF 370',
  'NMF AMR',
  'NMF COA',
  'NMF SIM',
  'OMC',
  'TTB',
] as const;

const PGA_MASTER_BASE_FIELD_MAP: Record<string, TemplateColumn> = {
  'part number': 'model_number',
  'part description': 'name',
  'country of origin': 'country_of_origin',
  'primary hts [ch. 1-97]': 'hts_code',
  'manufacturer name': 'manufacturer',
  'melted/poured country/region': 'country_of_smelt_pour',
};

interface PgaMasterUploadMetadata {
  baseColumnCount: number;
  pgaColumnCount: number;
  pgaSectionCount: number;
  populatedPgaRecords: number;
}

interface ParsedUploadFile {
  headers: string[];
  rows: string[][];
  pgaMaster?: PgaMasterUploadMetadata;
}

type ImportFileFormat = 'csv' | 'xlsx' | 'xml';

function getImportFileFormat(file: File): ImportFileFormat | null {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'csv' || extension === 'xlsx' || extension === 'xml') {
    return extension;
  }
  return null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const TEMPLATE_EXAMPLE_RECORDS: Array<Record<TemplateColumn, string>> = [
  {
    model_number: 'FD-3',
    parent: '',
    name: 'Falcon Drone Airframe FD-3',
    description: 'Carbon-fiber UAV airframe',
    traces: '',
    category: 'Airframe',
    hierarchy_type: 'Assembly',
    quantity: '1',
    unit_of_measure: 'units',
    manufacturer: 'Acme Aerospace',
    vendor: 'Acme Aerospace',
    specifications: 'Carbon fiber composite, 2.4m wingspan',
    country_of_origin: 'United States',
    hts_code: '8806.22.0000',
    trans_method: 'Air',
    country_of_smelt_pour: '',
    entry_date: '2025-01-15',
    loading_date: '2025-01-10',
    product_value: '52800',
    copper_value: '0',
    aluminum_value: '1200',
    steel_value: '0',
    copper_percent: '0',
    aluminum_percent: '15',
    steel_percent: '0',
  },
  {
    model_number: 'AEP-5',
    parent: 'FD-3',
    name: 'Aurora EO/IR Pod AEP-5',
    description: 'Electro-optical / infrared sensor pod',
    traces: '',
    category: 'Sensor Pod',
    hierarchy_type: 'Sub-Assembly',
    quantity: '2',
    unit_of_measure: 'units',
    manufacturer: 'Acme Aerospace',
    vendor: 'Acme Aerospace',
    specifications: 'Aluminum housing, germanium optics',
    country_of_origin: 'United States',
    hts_code: '9013.80.9000',
    trans_method: 'Air',
    country_of_smelt_pour: 'United States',
    entry_date: '2025-01-15',
    loading_date: '2025-01-10',
    product_value: '184500',
    copper_value: '500',
    aluminum_value: '3200',
    steel_value: '0',
    copper_percent: '2',
    aluminum_percent: '35',
    steel_percent: '0',
  },
  {
    model_number: 'CAM-12',
    parent: 'AEP-5',
    name: 'Aurora Camera Module CAM-12',
    description: 'Camera module for the Aurora sensor pod',
    traces: '',
    category: 'Camera Module',
    hierarchy_type: 'Component',
    quantity: '4',
    unit_of_measure: 'units',
    manufacturer: 'Acme Aerospace',
    vendor: 'Acme Aerospace',
    specifications: '12 MP optical camera module',
    country_of_origin: 'United States',
    hts_code: '8525.89.5050',
    trans_method: 'Air',
    country_of_smelt_pour: '',
    entry_date: '2025-01-15',
    loading_date: '2025-01-10',
    product_value: '12400',
    copper_value: '120',
    aluminum_value: '340',
    steel_value: '0',
    copper_percent: '3',
    aluminum_percent: '12',
    steel_percent: '0',
  },
];

export function csvEscape(v: string) {
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}
function buildTemplateCsv(databaseStructure: ImportDatabaseStructure) {
  const columns = STRUCTURE_TEMPLATE_COLUMNS[databaseStructure];
  const records =
    databaseStructure === 'hierarchy'
      ? TEMPLATE_EXAMPLE_RECORDS
      : TEMPLATE_EXAMPLE_RECORDS.slice(0, 2);
  const rows = [
    columns.join(','),
    ...records.map((record) =>
      columns.map((column) => csvEscape(record[column])).join(','),
    ),
  ];
  return rows.join('\n');
}
// Existing HTS-grouped databases have no hierarchy, so they get the flat template.
export function downloadTemplate(databaseStructure = 'hierarchy') {
  const structure: ImportDatabaseStructure =
    databaseStructure === 'hierarchy' ? 'hierarchy' : 'flat';
  const blob = new Blob([buildTemplateCsv(structure)], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download =
    structure === 'hierarchy'
      ? 'import_hub_bom_template.csv'
      : 'product_template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.info('Template download initiated.');
}

function downloadCargoWiseXmlExample() {
  const a = document.createElement('a');
  a.href = '/templates/cargowise-native-product-example.xml';
  a.download = 'cargowise-native-product-example.xml';
  document.body.appendChild(a);
  a.click();
  a.remove();
  toast.info('CargoWise XML example download initiated.');
}

/* ---------- Parsing ---------- */
// Exported for reuse by BulkCsvUploadDialog.tsx, which needs the same
// quote-aware handling (a naive split(",") / split("\n") breaks on any
// quoted comma or newline in a product name/description).
export function parseCsvText(text: string): {
  headers: string[];
  rows: string[][];
} {
  const out: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQ = false;
      } else field += c;
    } else {
      if (c === '"') inQ = true;
      else if (c === ',') {
        row.push(field);
        field = '';
      } else if (c === '\r') {
        /* skip */
      } else if (c === '\n') {
        row.push(field);
        out.push(row);
        row = [];
        field = '';
      } else field += c;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    out.push(row);
  }
  const cleaned = out.filter((r) => r.some((v) => (v ?? '').trim() !== ''));
  if (!cleaned.length) return { headers: [], rows: [] };
  const headers = cleaned[0].map((h) => h.trim());
  const rows = cleaned.slice(1);
  return { headers, rows };
}

function getPgaMasterLayout(rows: unknown[][]): {
  baseColumnCount: number;
  sections: Array<{ start: number; end: number }>;
} | null {
  if (rows.length < 3) return null;
  const programRow = rows[0] ?? [];
  const fieldRow = rows[2] ?? [];
  if (String(programRow[0] ?? '').trim() !== 'Base Commodity') return null;

  const sourceCodes = new Set<string>(PGA_MASTER_SOURCE_CODES);
  const starts = programRow
    .map((value, index) => ({ label: String(value ?? '').trim(), index }))
    .filter(({ label }) => label);
  if (
    !starts.some(({ label }) => label === 'FDA') ||
    !starts.slice(1).every(({ label }) => sourceCodes.has(label))
  ) {
    return null;
  }
  if (
    !fieldRow.some(
      (value) => String(value ?? '').trim() === 'Part Description',
    ) ||
    !fieldRow.some(
      (value) => String(value ?? '').trim() === 'Primary HTS [Ch. 1-97]',
    )
  ) {
    return null;
  }

  const baseColumnCount = starts[1]?.index ?? 0;
  if (baseColumnCount <= 0) return null;
  return {
    baseColumnCount,
    sections: starts.slice(1).map((start, index) => ({
      start: start.index,
      end: starts[index + 2]?.index ?? fieldRow.length,
    })),
  };
}

function parsePgaMasterRows(rows: unknown[][]): ParsedUploadFile | null {
  const layout = getPgaMasterLayout(rows);
  if (!layout) return null;
  const headers = (rows[2] ?? []).map((value) => String(value ?? '').trim());
  const dataRows = rows
    .slice(3)
    .map((row) => headers.map((_, index) => String(row[index] ?? '')));
  let populatedPgaRecords = 0;
  for (const row of dataRows) {
    for (const section of layout.sections) {
      if (
        row
          .slice(section.start, section.end)
          .some((value) => value.trim() !== '')
      ) {
        populatedPgaRecords += 1;
      }
    }
  }
  return {
    headers,
    rows: dataRows,
    pgaMaster: {
      baseColumnCount: layout.baseColumnCount,
      pgaColumnCount: Math.max(0, headers.length - layout.baseColumnCount),
      pgaSectionCount: layout.sections.length,
      populatedPgaRecords,
    },
  };
}

async function parseFile(file: File): Promise<ParsedUploadFile> {
  const isXlsx = /\.xlsx?$/i.test(file.name);
  if (isXlsx) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const aoa = XLSX.utils.sheet_to_json<string[]>(sheet, {
      header: 1,
      blankrows: false,
      defval: '',
    });
    if (!aoa.length) return { headers: [], rows: [] };
    if (getPgaMasterLayout(aoa as unknown[][])) {
      const formattedRows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        blankrows: false,
        defval: '',
        raw: false,
      });
      const pgaMaster = parsePgaMasterRows(formattedRows as unknown[][]);
      if (pgaMaster) return pgaMaster;
    }
    const headers = (aoa[0] as unknown[]).map((h) => String(h ?? '').trim());
    const rows = aoa
      .slice(1)
      .map((r) => (r as unknown[]).map((v) => String(v ?? '')));
    return { headers, rows };
  }
  // Column mapping only needs a bounded preview. The complete CSV is uploaded
  // unchanged and streamed by the product worker, so large imports are never
  // duplicated in browser memory merely to render this step.
  const text = await file.slice(0, 1024 * 1024).text();
  const parsed = parseCsvText(text);
  return { headers: parsed.headers, rows: parsed.rows.slice(0, 100) };
}

export interface UploadRowCountOptions {
  /** 0-based primary name column. A row counts when this column or one of
   * fallbackNameColIndices is non-blank. Omit to count every non-blank row
   * unfiltered (the historical behavior). */
  nameColIndex?: number;
  /** Additional mapped columns to use, in order, when the primary name cell
   * is blank. Import Hub resolves names as name -> description ->
   * model_number, matching the product worker. */
  fallbackNameColIndices?: number[];
  /** 0-based column index used with the resolved name to dedupe rows the
   * same way the import worker does for a brand-new database: a name +
   * country_of_origin pair already seen in this file won't consume capacity
   * twice. */
  countryColIndex?: number;
}

// Exported for reuse by BulkCsvUploadDialog.tsx. Unlike parseFile, this reads
// the entire file — the credit check needs the true row count, not the
// bounded preview used for column mapping. When `options.nameColIndex` is
// given, only rows that would actually consume capacity are counted, so the
// credit check agrees with what the import worker will actually insert
// instead of the raw parsed row count (see UploadRowCountOptions above).
export async function getUploadRowCount(
  file: File,
  options?: UploadRowCountOptions,
): Promise<number> {
  const {
    nameColIndex,
    fallbackNameColIndices = [],
    countryColIndex,
  } = options ?? {};

  if (/\.xlsx?$/i.test(file.name)) {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const aoa = XLSX.utils.sheet_to_json<string[]>(sheet, {
      header: 1,
      blankrows: false,
      defval: '',
    });
    const layout = getPgaMasterLayout(aoa as unknown[][]);
    const dataRows = aoa.slice(layout ? 3 : 1) as unknown[][];
    if (nameColIndex == null) return dataRows.length;
    const seen = new Set<string>();
    let count = 0;
    for (const row of dataRows) {
      const rowName = [nameColIndex, ...fallbackNameColIndices]
        .map((index) => String(row[index] ?? '').trim())
        .find(Boolean);
      if (!rowName) continue;
      const rowCountry =
        countryColIndex != null ? String(row[countryColIndex] ?? '') : '';
      // JSON-encode the pair rather than concatenating with a separator —
      // no delimiter choice is airtight against arbitrary product-name text.
      const key = JSON.stringify([rowName, rowCountry]);
      if (seen.has(key)) continue;
      seen.add(key);
      count++;
    }
    return count;
  }

  return countCsvRows(file, {
    nameColIndex,
    fallbackNameColIndices,
    countryColIndex,
  });
}

// Scans a CSV in bounded chunks so a very large file is never fully
// materialized in memory for what is only a row count — only a running
// counter, one in-flight row's fields, and (when filtering) a Set of seen
// name+origin keys are held at once, instead of parseCsvText's full
// per-field, per-row array for the whole file.
//
// Quote state persists across chunk boundaries via the outer-scope `field`/
// `row`/`inQuotes` variables, the same state machine as parseCsvText just
// fed one chunk at a time. The one genuinely ambiguous case — an escaped ""
// split exactly at a chunk boundary — is deferred to the next chunk via
// `carry` rather than guessed, since a lone trailing quote can't yet be told
// apart from a closing quote without seeing what follows it.
const ROW_COUNT_CHUNK_SIZE = 1024 * 1024; // 1MB

async function countCsvRows(
  file: File,
  {
    nameColIndex,
    fallbackNameColIndices = [],
    countryColIndex,
  }: UploadRowCountOptions,
): Promise<number> {
  const seen = nameColIndex != null ? new Set<string>() : null;
  let count = 0;
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let isHeaderRow = true;
  let carry = '';

  const commitRow = () => {
    row.push(field);
    field = '';
    if (row.some((v) => (v ?? '').trim() !== '')) {
      if (isHeaderRow) {
        isHeaderRow = false;
      } else if (seen) {
        const rowName = [nameColIndex as number, ...fallbackNameColIndices]
          .map((index) => (row[index] ?? '').trim())
          .find(Boolean);
        if (rowName) {
          const rowCountry =
            countryColIndex != null ? row[countryColIndex] ?? '' : '';
          const key = JSON.stringify([rowName, rowCountry]);
          if (!seen.has(key)) {
            seen.add(key);
            count++;
          }
        }
      } else {
        count++;
      }
    }
    row = [];
  };

  for (let offset = 0; offset < file.size; offset += ROW_COUNT_CHUNK_SIZE) {
    const isFinalChunk = offset + ROW_COUNT_CHUNK_SIZE >= file.size;
    const chunk =
      carry + (await file.slice(offset, offset + ROW_COUNT_CHUNK_SIZE).text());
    carry = '';

    for (let i = 0; i < chunk.length; i++) {
      const c = chunk[i];
      if (i === chunk.length - 1 && !isFinalChunk && inQuotes && c === '"') {
        carry = '"';
        break;
      }
      if (inQuotes) {
        if (c === '"') {
          if (chunk[i + 1] === '"') {
            field += '"';
            i++;
          } else inQuotes = false;
        } else field += c;
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        row.push(field);
        field = '';
      } else if (c === '\r') {
        /* skip */
      } else if (c === '\n') {
        commitRow();
      } else {
        field += c;
      }
    }
  }
  if (field.length > 0 || row.length > 0) {
    commitRow();
  }
  return count;
}

/* ---------- Helpers ----------
 * normHeader/autoMap/import mapping helpers are exported for reuse by any
 * other flow that bulk-uploads products against the real product service
 * (e.g. BulkCsvUploadDialog's Import Data action). The resolved header metadata
 * should have exactly one implementation, not be re-derived per caller.
 */
export function normHeader(s: string) {
  return (
    s
      // Invisible control characters (e.g. a stray \x1F from an ERP export)
      // would otherwise end up inside the stored column name.
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
  );
}

// Column keys the product service treats as real product fields (or special
// cases) rather than additional columns -- an unmapped column must never
// resolve to one of these (product.service.ts PRODUCT_TEMPLATE_COLUMNS plus
// unit_value / external_id / import_source handling in buildProductImportRecord).
const RESERVED_IMPORT_KEYS = new Set<string>([
  ...TEMPLATE_COLUMNS,
  'unit_value',
  'external_id',
  'import_source',
]);
export function autoMap(
  headers: string[],
  pgaMaster?: PgaMasterUploadMetadata,
  allowedColumns: readonly TemplateColumn[] = TEMPLATE_COLUMNS,
): Record<number, TemplateColumn | ''> {
  const map: Record<number, TemplateColumn | ''> = {};
  const used = new Set<string>();
  headers.forEach((h, i) => {
    if (pgaMaster && i < pgaMaster.baseColumnCount) {
      const pgaBaseMatch = PGA_MASTER_BASE_FIELD_MAP[h.trim().toLowerCase()];
      if (pgaBaseMatch && !used.has(pgaBaseMatch)) {
        map[i] = pgaBaseMatch;
        used.add(pgaBaseMatch);
        return;
      }
    }
    const n = normHeader(h);
    const found = allowedColumns.find((c) => c === n && !used.has(c));
    if (found) {
      map[i] = found;
      used.add(found);
    } else map[i] = '';
  });
  return map;
}

/**
 * Resolve the user's mapping once for both supported paths: streaming CSVs
 * send this header metadata alongside the original file, while the XLSX
 * fallback uses it when producing a CSV blob. Unmapped keys are retained in
 * additional_columns by the product service.
 */
export function buildProductImportMetadata(
  headers: string[],
  mapping: Record<number, TemplateColumn | ''>,
  pgaMaster?: PgaMasterUploadMetadata,
): {
  resolvedHeaders: string[];
  notMappedColumns: Record<string, string>;
} {
  // The product service keys each CSV row by these names, so two columns
  // resolving to the same key silently overwrite each other (four "UQ"
  // columns kept only the last). Every unmapped column gets a unique key that
  // can't collide with a mapped field: "uq", "uq_2", "uq_3"...
  const usedKeys = new Set<string>(
    headers.map((_h, i) => mapping[i]).filter(Boolean) as string[],
  );
  const uniqueKey = (base: string) => {
    let key = base;
    for (let n = 2; usedKeys.has(key) || RESERVED_IMPORT_KEYS.has(key); n++) {
      key = `${base}_${n}`;
    }
    usedKeys.add(key);
    return key;
  };
  const resolvedHeaders = headers.map((h, i) => {
    if (mapping[i]) return mapping[i] as string;
    if (pgaMaster && i >= pgaMaster.baseColumnCount) return h.trim();
    return uniqueKey(normHeader(h) || `column_${i + 1}`);
  });
  const notMappedColumns: Record<string, string> = {};
  headers.forEach((_header, i) => {
    if (!mapping[i] && (!pgaMaster || i < pgaMaster.baseColumnCount)) {
      notMappedColumns[resolvedHeaders[i]] = resolvedHeaders[i];
    }
  });
  return { resolvedHeaders, notMappedColumns };
}

export function buildMappedProductsCsv(
  headers: string[],
  rows: string[][],
  mapping: Record<number, TemplateColumn | ''>,
  nameColIdx: number,
  pgaMaster?: PgaMasterUploadMetadata,
): { csvBlob: Blob; notMappedColumns: Record<string, string> } {
  const { resolvedHeaders, notMappedColumns } = buildProductImportMetadata(
    headers,
    mapping,
    pgaMaster,
  );

  const csvLines = [resolvedHeaders.map(csvEscape).join(',')];
  const fallbackNameColIndices = Object.entries(mapping)
    .filter(([, field]) => field === 'description' || field === 'model_number')
    .sort(([, left], [, right]) =>
      left === right ? 0 : left === 'description' ? -1 : 1,
    )
    .map(([index]) => Number(index));
  rows.forEach((r) => {
    const resolvedName = [nameColIdx, ...fallbackNameColIndices]
      .filter((index) => index >= 0)
      .map((index) => (r[index] ?? '').trim())
      .find(Boolean);
    if (!resolvedName) return;
    csvLines.push(r.map((v) => csvEscape(v ?? '')).join(','));
  });

  return {
    csvBlob: new Blob([csvLines.join('\n')], { type: 'text/csv' }),
    notMappedColumns,
  };
}

/* ---------- Screens ---------- */
type StepKey = 'setup' | 'upload' | 'documents' | 'screening';
const STEP_LABEL: Record<StepKey, string> = {
  setup: 'Set up database',
  upload: 'Upload products',
  documents: 'Upload your template',
  screening: 'Screening',
};

const formatTemplateColumn = (column: TemplateColumn): string =>
  column
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/* "Use your template": what PyAPI's column-mapping agent decided for each
 * column, kept beside `mapping` so the panel can badge AI matches and the
 * submit path can normalize values with the agent's format hints. */
interface PlannedColumnMeta {
  target: TemplateColumn | null;
  source: MappingSource;
  confidence: number;
  reason?: string | null;
  hints: ColumnHints;
  valueMap?: Record<string, string> | null;
}
type AiMappingStatus = 'idle' | 'running' | 'done' | 'failed';
type MappingFilter = 'all' | 'mapped' | 'check' | 'additional';

// Shown while the column-mapping agent works -- wide files take 15-30s, so
// the message moves on and an elapsed counter ticks to show it's still alive.
const COLUMN_MATCHING_MESSAGES: Array<[number, string]> = [
  [0, 'Reading your columns…'],
  [4, 'Matching your columns to BITE fields…'],
  [15, 'Still matching — files with many columns can take up to a minute.'],
];

const ColumnMatchingStatus: React.FC<{ fileName?: string }> = ({
  fileName,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(
      () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, []);
  const message = COLUMN_MATCHING_MESSAGES.reduce(
    (current, [after, text]) => (elapsedSeconds >= after ? text : current),
    COLUMN_MATCHING_MESSAGES[0][1],
  );
  return (
    <div className='ih-map-working'>
      <span className='ih-map-working-icon' aria-hidden='true'>
        <Sparkles size={28} />
      </span>
      <div
        className='ih-font-medium ih-text-sm'
        role='status'
        aria-live='polite'
      >
        {message}
      </div>
      <div className='ih-map-working-progress' aria-hidden='true'>
        <div className='ih-map-working-progress-fill' />
      </div>
      <div className='ih-text-xs ih-text-muted ih-map-working-meta'>
        {fileName ? `${fileName} · ` : ''}
        <span aria-hidden='true'>{elapsedSeconds}s</span>
      </div>
    </div>
  );
};

const isPdfFile = (f: File) =>
  f.type === 'application/pdf' || /\.pdf$/i.test(f.name);

const SCREENING_FREQUENCIES = [
  'Once',
  'Daily',
  'Weekly',
  'Monthly',
  'Yearly',
] as const;
const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
export const DATABASE_NOTIFICATION_CHANNELS = [
  'Email Notification',
  'In-app Notification',
  'Daily Updates Notification',
] as const;
const DEFAULT_DATABASE_NOTIFICATION_CHANNELS = ['In-app Notification'];
const DATABASE_NOTIFICATION_DESCRIPTIONS: Record<
  (typeof DATABASE_NOTIFICATION_CHANNELS)[number],
  string
> = {
  'Email Notification': 'Send screening-result notifications by email.',
  'In-app Notification': 'Notifications appear directly in BITE.',
  'Daily Updates Notification': 'Include results in the daily updates feed.',
};

/* ---------- Shared style objects ---------- */
const FONT_ROBOTO = 'Roboto, Inter, system-ui, sans-serif';
const FONT_INTER = 'Inter, system-ui, sans-serif';

const inputStyle: CSSProperties = {
  width: '100%',
  minHeight: 36,
  padding: '8px 12px',
  borderRadius: 8,
  fontSize: 13,
  background: 'var(--bg-colors)',
  color: 'var(--text-primary)',
  border: '1px solid var(--edge-soft)',
  outline: 'none',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
  fontFamily: FONT_ROBOTO,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  fontFamily: FONT_ROBOTO,
  marginBottom: 6,
};

/* ---------- Component ---------- */
export const CreateImportDatabaseDialog: React.FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated?: () => void;
  /** Called instead of onCreated when startMode === 'documents': the caller
   * hands off into the embedded document-upload view for this database
   * rather than the normal post-create refresh. */
  onCreatedForDocumentUpload?: (
    db: { id: string; name: string },
    file: File,
  ) => void;
  /** cur_user.selected_team.id — required to create a real database. */
  groupId?: string;
  /** cur_user.basics.id — required to create a real database. */
  createdBy?: string;
}> = ({
  open,
  onOpenChange,
  onCreated,
  onCreatedForDocumentUpload,
  groupId,
  createdBy,
}) => {
  const history = useHistory();
  const [stepIdx, setStepIdx] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [iconKey, setIconKey] = useState<string>(DEFAULT_IMPORT_DATABASE_ICON);
  const [accent, setAccent] = useState<string>(DEFAULT_IMPORT_DATABASE_ACCENT);
  const [databaseStructure, setDatabaseStructure] =
    useState<ImportDatabaseStructure>('hierarchy');
  const [startMode, setStartMode] = useState<'manual' | 'file' | 'documents'>(
    'file',
  );
  const [file, setFile] = useState<File | null>(null);
  const [fileFormat, setFileFormat] = useState<ImportFileFormat | null>(null);
  // "Upload documents" start mode: the customs-entry PDF attached here rides
  // along in router state and lands already staged in the Customs Entry
  // Analyzer's own composer -- see the 'documents' step below and onSubmit.
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentDragActive, setDocumentDragActive] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [pgaMaster, setPgaMaster] = useState<PgaMasterUploadMetadata | null>(
    null,
  );
  const [mapping, setMapping] = useState<Record<number, TemplateColumn | ''>>(
    {},
  );
  const [dragActive, setDragActive] = useState(false);
  const [rejectType, setRejectType] = useState(false);
  const [fileParsing, setFileParsing] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<any>(null);
  const [pendingAction, setPendingAction] = useState<
    'PUSH' | 'REPLACE' | 'POP'
  >('PUSH');
  const [saving, setSaving] = useState(false);
  const [screeningFrequency, setScreeningFrequency] = useState('Once');
  const [screeningDayOfWeek, setScreeningDayOfWeek] = useState('Monday');
  const [notificationChannels, setNotificationChannels] = useState<string[]>(
    () => [...DEFAULT_DATABASE_NOTIFICATION_CHANNELS],
  );
  const [importJob, setImportJob] = useState<ImportProductJobState | null>(
    null,
  );
  const [creditBlock, setCreditBlock] = useState<ProductCreditCheck | null>(
    null,
  );
  const [pendingImportDatabaseId, setPendingImportDatabaseId] = useState<
    string | null
  >(null);
  // "Use your template" spreadsheet state (startMode === 'documents').
  const [aiMappingStatus, setAiMappingStatus] =
    useState<AiMappingStatus>('idle');
  const [plannedColumns, setPlannedColumns] = useState<
    Record<number, PlannedColumnMeta>
  >({});
  const [userMappedColumns, setUserMappedColumns] = useState<Set<number>>(
    () => new Set(),
  );
  const [mappingWarnings, setMappingWarnings] = useState<string[]>([]);
  const [templateHeaderRowIndex, setTemplateHeaderRowIndex] = useState(0);
  // Full rows (from the top, incl. any title block) of the XLSX sheet the
  // agent chose -- what the normalized upload is rebuilt from.
  const templateSheetRowsRef = useRef<string[][] | null>(null);
  const aiMappingAbortRef = useRef<AbortController | null>(null);
  // Column review (both upload options): filter chips, search, and the
  // mapping the rows are *ordered* by -- captured when a mapping is applied,
  // not live, so changing a dropdown never moves the row being edited.
  const [mappingFilter, setMappingFilter] = useState<MappingFilter>('all');
  const [mappingSearch, setMappingSearch] = useState('');
  const [reviewOrderMapping, setReviewOrderMapping] = useState<
    Record<number, TemplateColumn | ''>
  >({});
  const pollGenerationRef = useRef(0);
  const fileSelectionGenerationRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const documentFileInputRef = useRef<HTMLInputElement>(null);
  const allowNavigationRef = useRef(false);

  const steps: StepKey[] =
    startMode === 'file'
      ? ['setup', 'upload', 'screening']
      : startMode === 'documents'
      ? ['setup', 'documents', 'screening']
      : ['setup', 'screening'];
  const currentStep = steps[Math.min(stepIdx, steps.length - 1)];

  useEffect(() => {
    if (stepIdx > steps.length - 1) setStepIdx(steps.length - 1);
  }, [steps.length, stepIdx]);

  function resetAll() {
    setStepIdx(0);
    setName('');
    setDescription('');
    setIconKey(DEFAULT_IMPORT_DATABASE_ICON);
    setAccent(DEFAULT_IMPORT_DATABASE_ACCENT);
    setDatabaseStructure('hierarchy');
    setStartMode('file');
    setFile(null);
    setFileFormat(null);
    setDocumentFile(null);
    setDocumentDragActive(false);
    setHeaders([]);
    setRows([]);
    setPgaMaster(null);
    setMapping({});
    setDragActive(false);
    setRejectType(false);
    setFileParsing(false);
    setAppearanceOpen(false);
    setMappingOpen(false);
    setDiscardConfirmOpen(false);
    setPendingLocation(null);
    setPendingAction('PUSH');
    setScreeningFrequency('Once');
    setScreeningDayOfWeek('Monday');
    setNotificationChannels([...DEFAULT_DATABASE_NOTIFICATION_CHANNELS]);
    setSaving(false);
    setImportJob(null);
    setCreditBlock(null);
    setPendingImportDatabaseId(null);
    resetColumnMappingState();
    pollGenerationRef.current += 1;
    fileSelectionGenerationRef.current += 1;
  }

  function resetColumnMappingState() {
    setMappingFilter('all');
    setMappingSearch('');
    setReviewOrderMapping({});
    aiMappingAbortRef.current?.abort();
    aiMappingAbortRef.current = null;
    templateSheetRowsRef.current = null;
    setAiMappingStatus('idle');
    setPlannedColumns({});
    setUserMappedColumns(new Set());
    setMappingWarnings([]);
    setTemplateHeaderRowIndex(0);
  }

  // Drops the selected spreadsheet and everything derived from it. Bumping
  // the generation makes any in-flight parse / mapping request a no-op.
  function clearSelectedFile() {
    fileSelectionGenerationRef.current += 1;
    setFile(null);
    setFileFormat(null);
    setHeaders([]);
    setRows([]);
    setPgaMaster(null);
    setMapping({});
    setMappingOpen(false);
    setFileParsing(false);
    setRejectType(false);
    resetColumnMappingState();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // The two upload modes share the file/mapping state, so a file picked under
  // one start option never silently carries over into the other.
  function selectStartMode(next: 'manual' | 'file' | 'documents') {
    if (next === startMode) return;
    clearSelectedFile();
    setDocumentFile(null);
    setStartMode(next);
  }

  const hasUnsavedChanges =
    !!name.trim() ||
    !!description.trim() ||
    startMode !== 'file' ||
    !!file ||
    iconKey !== DEFAULT_IMPORT_DATABASE_ICON ||
    accent !== DEFAULT_IMPORT_DATABASE_ACCENT ||
    screeningFrequency !== 'Once' ||
    notificationChannels.length !==
      DEFAULT_DATABASE_NOTIFICATION_CHANNELS.length ||
    notificationChannels.some(
      (channel) => !DEFAULT_DATABASE_NOTIFICATION_CHANNELS.includes(channel),
    );
  const shouldWarnOnLeave = open && hasUnsavedChanges && !saving;

  useEffect(() => {
    if (!shouldWarnOnLeave) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldWarnOnLeave]);

  const handleBlockedNavigation = (
    nextLocation: any,
    action: 'PUSH' | 'REPLACE' | 'POP',
  ): boolean => {
    const isCreditPurchaseNavigation =
      !!creditBlock &&
      nextLocation.pathname === '/profile' &&
      nextLocation.state?.linkTab === 'Subscriptions';
    if (
      allowNavigationRef.current ||
      !shouldWarnOnLeave ||
      isCreditPurchaseNavigation
    ) {
      return true;
    }
    setPendingLocation(nextLocation);
    setPendingAction(action);
    setDiscardConfirmOpen(true);
    return false;
  };

  function handleOpenChange(v: boolean) {
    if (saving) return;
    if (v) {
      onOpenChange(true);
      return;
    }
    if (hasUnsavedChanges) {
      setDiscardConfirmOpen(true);
      return;
    }
    resetAll();
    onOpenChange(false);
  }

  function discardAndClose() {
    const navigationTarget = pendingLocation;
    const navigationAction = pendingAction;
    setPendingLocation(null);
    setPendingAction('PUSH');
    setDiscardConfirmOpen(false);
    if (navigationTarget) allowNavigationRef.current = true;
    // Let the confirmation Dialog release its body lock before the outer
    // Dialog closes and restores the page's original overflow value.
    window.setTimeout(() => {
      resetAll();
      onOpenChange(false);
      if (navigationTarget) {
        if (navigationAction === 'PUSH') {
          history.push(navigationTarget);
        } else {
          // REPLACE and POP both replace the current entry. Pushing on a POP
          // left the discarded dialog route sitting one step behind the
          // destination, so a second Back returned the user to the page they
          // had just confirmed leaving, and it truncated forward history too.
          // Prompt does not expose the POP delta, so neither branch can replay
          // the real traversal -- but replace at least leaves the surrounding
          // stack intact instead of adding a duplicate entry.
          history.replace(navigationTarget);
        }
        window.setTimeout(() => {
          allowNavigationRef.current = false;
        }, 0);
      }
    }, 0);
  }

  function keepEditing() {
    setPendingLocation(null);
    setPendingAction('PUSH');
    setDiscardConfirmOpen(false);
  }

  const SelectedIcon = getImportDatabaseIcon(iconKey);

  const mappedCount = useMemo(
    () => Object.values(mapping).filter((v) => v).length,
    [mapping],
  );
  const nameColIdx = useMemo(() => {
    const entry = Object.entries(mapping).find(([, v]) => v === 'name');
    return entry ? Number(entry[0]) : -1;
  }, [mapping]);
  const importNameColIndices = useMemo(() => {
    const mappedIndex = (field: TemplateColumn) => {
      const entry = Object.entries(mapping).find(
        ([, value]) => value === field,
      );
      return entry ? Number(entry[0]) : -1;
    };
    return [
      nameColIdx,
      mappedIndex('description'),
      mappedIndex('model_number'),
    ].filter((index) => index >= 0);
  }, [mapping, nameColIdx]);
  const countryColIdx = useMemo(() => {
    const entry = Object.entries(mapping).find(
      ([, v]) => v === 'country_of_origin',
    );
    return entry ? Number(entry[0]) : -1;
  }, [mapping]);
  const htsCodeColIdx = useMemo(() => {
    const entry = Object.entries(mapping).find(([, v]) => v === 'hts_code');
    return entry ? Number(entry[0]) : -1;
  }, [mapping]);
  const fallbackNamePreviewCount = useMemo(() => {
    if (!file || fileFormat === 'xml') return 0;
    const fallbackIndices = importNameColIndices.filter(
      (index) => index !== nameColIdx,
    );
    if (fallbackIndices.length === 0) return 0;
    return rows.filter((row) => {
      if (nameColIdx >= 0 && (row[nameColIdx] ?? '').trim()) return false;
      if (
        pgaMaster &&
        (htsCodeColIdx < 0 || !(row[htsCodeColIdx] ?? '').trim())
      )
        return false;
      return fallbackIndices.some((index) => (row[index] ?? '').trim());
    }).length;
  }, [
    file,
    fileFormat,
    htsCodeColIdx,
    importNameColIndices,
    nameColIdx,
    pgaMaster,
    rows,
  ]);
  const importCount = useMemo(() => {
    if (importNameColIndices.length === 0) return 0;
    return rows.filter(
      (r) =>
        importNameColIndices.some((index) => (r[index] ?? '').trim() !== '') &&
        (!pgaMaster ||
          (htsCodeColIdx >= 0 && (r[htsCodeColIdx] ?? '').trim() !== '')),
    ).length;
  }, [rows, importNameColIndices, htsCodeColIdx, pgaMaster]);
  const mappingNeedsAttention =
    !!file &&
    fileFormat !== 'xml' &&
    (importNameColIndices.length === 0 || (!!pgaMaster && htsCodeColIdx < 0));
  const orderedMappingIndices = useMemo(() => {
    const visibleHeaderCount = pgaMaster?.baseColumnCount ?? headers.length;
    const importantFields = new Set<TemplateColumn>([
      'name',
      'description',
      'model_number',
    ]);
    if (pgaMaster) importantFields.add('hts_code');

    // Matches that need a look first, then the product-identity fields,
    // other matches, and last the columns kept as additional columns.
    const priority = (index: number) => {
      const mappedField = reviewOrderMapping[index];
      if (!mappedField) return 3;
      const planned = plannedColumns[index];
      if (
        planned?.target === mappedField &&
        planned.confidence < LOW_CONFIDENCE_THRESHOLD
      ) {
        return 0;
      }
      return importantFields.has(mappedField) ? 1 : 2;
    };

    return headers
      .slice(0, visibleHeaderCount)
      .map((_, index) => index)
      .sort((a, b) => priority(a) - priority(b) || a - b);
  }, [headers, reviewOrderMapping, plannedColumns, pgaMaster]);
  // First non-blank value of each column, shown under its name in review.
  const columnSamples = useMemo(
    () =>
      headers.map(
        (_, index) =>
          rows.find((row) => (row[index] ?? '').trim())?.[index]?.trim() ?? '',
      ),
    [headers, rows],
  );
  const setupIsValid = !saving && !!name.trim() && !!groupId && !!createdBy;
  // "Use your template" with a CSV/XLSX attached imports through the same
  // bulk-upload path as "Import from file"; a PDF still hands off to the
  // Document Upload tool.
  const isTemplateSpreadsheet = startMode === 'documents' && !!file;
  // "Use your template" hides the Hierarchy / Flat choice. Its mapping offers
  // every field, and the database becomes a BOM hierarchy only when the file
  // actually has a Parent or Hierarchy Type column; otherwise it's flat.
  const mappingStructure: ImportDatabaseStructure =
    startMode === 'documents' ? 'hierarchy' : databaseStructure;
  const importsProducts = startMode === 'file' || isTemplateSpreadsheet;
  const aiMappingRunning = aiMappingStatus === 'running';
  // An AI match the agent was unsure about and the user hasn't touched.
  const isUncertainMatch = (index: number) => {
    const meta = plannedColumns[index];
    return (
      isTemplateSpreadsheet &&
      !userMappedColumns.has(index) &&
      !!meta?.target &&
      mapping[index] === meta.target &&
      meta.confidence < LOW_CONFIDENCE_THRESHOLD
    );
  };
  const lowConfidenceCount = headers.filter((_, index) =>
    isUncertainMatch(index),
  ).length;
  // An uncertain Parent / Hierarchy Type match only makes the database a
  // hierarchy once the user confirms or picks it.
  const templateStructure: ImportDatabaseStructure = Object.entries(
    mapping,
  ).some(
    ([index, field]) =>
      (field === 'parent' || field === 'hierarchy_type') &&
      !isUncertainMatch(Number(index)),
  )
    ? 'hierarchy'
    : 'flat';
  // Leave the "Needs check" view once the last uncertain match is resolved.
  useEffect(() => {
    if (mappingFilter === 'check' && lowConfidenceCount === 0) {
      setMappingFilter('all');
    }
  }, [mappingFilter, lowConfidenceCount]);
  const fileIsReady =
    !importsProducts ||
    (!!file &&
      !!fileFormat &&
      !fileParsing &&
      (fileFormat === 'xml' ||
        (rows.length > 0 &&
          !mappingNeedsAttention &&
          // Every tabular import, not just PGA Master. rows.length only proves
          // the file has lines -- if the mapped name column is blank on all of
          // them, importCount is 0 and we would create a database with no
          // products in it.
          importCount > 0)));
  const documentsStepIsReady =
    startMode !== 'documents' ||
    !!documentFile ||
    (isTemplateSpreadsheet &&
      fileIsReady &&
      !aiMappingRunning &&
      // Uncertain matches must be confirmed or changed before import, so a
      // guess can't silently fill the wrong field.
      lowConfidenceCount === 0);
  const canSubmit = setupIsValid && fileIsReady && documentsStepIsReady;
  // The column-mapping banners/panel render on the "Import from file" upload
  // step and on the "Use your template" step once a spreadsheet is attached.
  const showsColumnMapping =
    currentStep === 'upload' ||
    (currentStep === 'documents' && isTemplateSpreadsheet);
  const reviewingColumns =
    showsColumnMapping &&
    !aiMappingRunning &&
    !!file &&
    fileFormat !== 'xml' &&
    (mappingOpen || mappingNeedsAttention);
  const mappingFilterCounts: Record<MappingFilter, number> = {
    all: orderedMappingIndices.length,
    mapped: orderedMappingIndices.filter((i) => !!mapping[i]).length,
    check: orderedMappingIndices.filter(isUncertainMatch).length,
    additional: orderedMappingIndices.filter((i) => !mapping[i]).length,
  };
  const mappingSearchText = mappingSearch.trim().toLowerCase();
  const visibleMappingIndices = orderedMappingIndices.filter((i) => {
    if (mappingFilter === 'mapped' && !mapping[i]) return false;
    if (mappingFilter === 'additional' && mapping[i]) return false;
    if (mappingFilter === 'check' && !isUncertainMatch(i)) return false;
    if (!mappingSearchText) return true;
    const field = mapping[i];
    return [
      headers[i],
      columnSamples[i],
      field ? formatTemplateColumn(field) : '',
    ].some((text) => (text ?? '').toLowerCase().includes(mappingSearchText));
  });
  const currentStepIsReady =
    currentStep === 'setup'
      ? setupIsValid
      : currentStep === 'documents'
      ? documentsStepIsReady
      : fileIsReady;

  const savingStatusText = useMemo(() => {
    if (!importJob) {
      return importsProducts ? 'Preparing import…' : 'Creating database…';
    }
    const { status, processedRows, totalRows } = importJob;
    switch (status) {
      case 'queued':
        return 'Preparing import…';
      case 'validating':
        return totalRows > 0
          ? `Validating rows… ${processedRows} of ${totalRows}`
          : 'Validating rows…';
      case 'importing':
        return totalRows > 0
          ? `Importing products… ${processedRows} of ${totalRows}`
          : 'Importing products…';
      case 'finalizing':
        return 'Finalizing import…';
      case 'cancelled':
        return 'Import cancelled';
      default:
        return importsProducts ? 'Preparing import…' : 'Creating database…';
    }
  }, [importJob, importsProducts]);

  function selectDatabaseStructure(next: ImportDatabaseStructure) {
    if (next === databaseStructure) return;
    setDatabaseStructure(next);
    if (!file && !headers.length) return;
    clearSelectedFile();
    toast.info(
      'Structure changed. Upload your file again for the new template.',
    );
  }

  async function acceptFile(f: File, options?: { aiMapping?: boolean }) {
    const selectionGeneration = fileSelectionGenerationRef.current + 1;
    fileSelectionGenerationRef.current = selectionGeneration;
    setFile(null);
    setFileFormat(null);
    setHeaders([]);
    setRows([]);
    setPgaMaster(null);
    setMapping({});
    setMappingOpen(false);
    setFileParsing(false);
    resetColumnMappingState();

    const nextFileFormat = getImportFileFormat(f);
    if (!nextFileFormat) {
      setRejectType(true);
      toast.error('Unsupported file type. Upload a CSV, XLSX, or XML file.');
      setTimeout(() => setRejectType(false), 2200);
      return;
    }
    if (f.size === 0) {
      setRejectType(true);
      toast.error('That file is empty. Choose a file containing product data.');
      setTimeout(() => setRejectType(false), 2200);
      return;
    }

    setRejectType(false);
    if (nextFileFormat === 'xml') {
      setFile(f);
      setFileFormat(nextFileFormat);
      return;
    }

    setFileParsing(true);
    try {
      const {
        headers: h,
        rows: r,
        pgaMaster: detectedPgaMaster,
      } = await parseFile(f);
      if (fileSelectionGenerationRef.current !== selectionGeneration) return;
      setFile(f);
      setFileFormat(nextFileFormat);
      setHeaders(h);
      setRows(r);
      setPgaMaster(detectedPgaMaster ?? null);
      const nextMapping = autoMap(
        h,
        detectedPgaMaster,
        STRUCTURE_TEMPLATE_COLUMNS[
          options?.aiMapping ? 'hierarchy' : databaseStructure
        ],
      );
      setMapping(nextMapping);
      setReviewOrderMapping(nextMapping);
      const mappedValues = Object.values(nextMapping);
      const hasProductIdentity = mappedValues.some(
        (value) =>
          value === 'name' ||
          value === 'description' ||
          value === 'model_number',
      );
      const hasRequiredHts =
        !detectedPgaMaster || mappedValues.includes('hts_code');
      setMappingOpen(!hasProductIdentity || !hasRequiredHts);
      if (options?.aiMapping) {
        if (nextFileFormat === 'xlsx') {
          templateSheetRowsRef.current = [h, ...r];
        }
        // The PGA Master layout already has a deterministic field map.
        if (!detectedPgaMaster) {
          void applyAiMapping(f, nextFileFormat, h, r, selectionGeneration);
        }
      }
    } catch {
      if (fileSelectionGenerationRef.current !== selectionGeneration) return;
      toast.error("Couldn't parse that file.");
    } finally {
      if (fileSelectionGenerationRef.current === selectionGeneration) {
        setFileParsing(false);
      }
    }
  }

  // Asks PyAPI's column-mapping agent for the best mapping and applies it
  // automatically. The exact-header matches acceptFile already applied stay
  // in place if the request fails, so the user can always map by hand.
  async function applyAiMapping(
    f: File,
    format: 'csv' | 'xlsx',
    previewHeaders: string[],
    previewRows: string[][],
    selectionGeneration: number,
  ) {
    if (!groupId) return;
    const isCurrent = () =>
      fileSelectionGenerationRef.current === selectionGeneration;
    const controller = new AbortController();
    aiMappingAbortRef.current = controller;
    setAiMappingStatus('running');
    try {
      const workbookSheets: WorkbookSheet[] | undefined =
        format === 'xlsx' ? await readWorkbookSheets(f) : undefined;
      if (!isCurrent()) return;
      // A template is mapped against every field, Parent and Hierarchy Type
      // included; the database's structure then follows from what got
      // mapped (templateStructure).
      const profile = await buildFileProfile(f, format, {
        groupId,
        databaseStructure: 'hierarchy',
        workbookSheets,
      });
      if (!isCurrent()) return;
      const plan: ColumnMappingPlan =
        await ImportMappingAPI.suggestColumnMapping(profile, controller.signal);
      if (!isCurrent()) return;

      // Re-slice the preview when the agent found the table on another sheet
      // or under a title block. Row indices count non-blank rows, the same
      // way parseFile and the profile read the file.
      const headerRow = Math.max(0, plan.header_row_index);
      const chosenSheet =
        workbookSheets?.find((s) => s.name === plan.sheet_name) ??
        workbookSheets?.[0];
      const sourceRows = chosenSheet
        ? chosenSheet.rows
        : [previewHeaders, ...previewRows];
      if (format === 'xlsx') templateSheetRowsRef.current = sourceRows;
      const width = Math.max(
        plan.columns.length,
        sourceRows[headerRow]?.length ?? 0,
      );
      const nextHeaders = Array.from({ length: width }, (_, i) =>
        String(sourceRows[headerRow]?.[i] ?? '').trim(),
      );
      setHeaders(nextHeaders);
      setRows(sourceRows.slice(headerRow + 1));
      setTemplateHeaderRowIndex(headerRow);

      const allowed = new Set<string>(STRUCTURE_TEMPLATE_COLUMNS.hierarchy);
      const nextMapping: Record<number, TemplateColumn | ''> = {};
      const nextPlanned: Record<number, PlannedColumnMeta> = {};
      nextHeaders.forEach((_, i) => {
        nextMapping[i] = '';
      });
      // One column per field. pyapi already guarantees it, but two columns
      // resolving to the same field would silently overwrite each other on
      // import, so keep only the most confident claim here as well.
      const claimedTargets = new Set<string>();
      [...plan.columns]
        .sort((a, b) => b.confidence - a.confidence)
        .forEach((column) => {
          let target =
            column.target && allowed.has(column.target)
              ? (column.target as TemplateColumn)
              : null;
          if (target && claimedTargets.has(target)) target = null;
          if (target) {
            claimedTargets.add(target);
            nextMapping[column.index] = target;
          }
          nextPlanned[column.index] = {
            target,
            source: column.source,
            confidence: column.confidence,
            reason: column.reason,
            hints: column.hints ?? {},
            valueMap: column.value_map,
          };
        });
      setMapping(nextMapping);
      setReviewOrderMapping(nextMapping);
      setPlannedColumns(nextPlanned);
      setMappingWarnings(plan.warnings ?? []);
      setAiMappingStatus(plan.used_ai ? 'done' : 'failed');
      const mapped = Object.values(nextMapping);
      const uncertainCount = Object.values(nextPlanned).filter(
        (meta) => meta.target && meta.confidence < LOW_CONFIDENCE_THRESHOLD,
      ).length;
      if (uncertainCount > 0) {
        // Open straight onto the matches that need the user's confirmation.
        setMappingOpen(true);
        setMappingFilter('check');
      } else {
        setMappingOpen(
          !mapped.some(
            (v) => v === 'name' || v === 'description' || v === 'model_number',
          ),
        );
      }
    } catch {
      if (!isCurrent()) return;
      setAiMappingStatus('failed');
      setMappingWarnings([
        'Automatic column matching is unavailable right now, so only exact column names were matched. Please review the mapping.',
      ]);
    } finally {
      if (aiMappingAbortRef.current === controller) {
        aiMappingAbortRef.current = null;
      }
    }
  }

  // "Use your template" accepts a customs-entry PDF (handed off to the
  // Document Upload tool after the database is created) or the user's own
  // CSV/XLSX product file (mapped by the agent and imported here).
  function handleTemplateFile(f: File) {
    if (isPdfFile(f)) {
      clearSelectedFile();
      setDocumentFile(f);
      return;
    }
    const format = getImportFileFormat(f);
    if (format === 'csv' || format === 'xlsx') {
      setDocumentFile(null);
      void acceptFile(f, { aiMapping: true });
      return;
    }
    setRejectType(true);
    toast.error('Unsupported file type. Upload a CSV, XLSX, or PDF file.');
    setTimeout(() => setRejectType(false), 2200);
  }

  function setColumnMapping(index: number, value: TemplateColumn | '') {
    setMapping((prev) => ({ ...prev, [index]: value }));
    setUserMappedColumns((prev) => new Set(prev).add(index));
  }

  // Accepting an uncertain match counts the same as the user picking it.
  function confirmColumnMatch(index: number) {
    setUserMappedColumns((prev) => new Set(prev).add(index));
  }

  function confirmAllUncertainMatches() {
    setUserMappedColumns((prev) => {
      const next = new Set(prev);
      headers.forEach((_h, index) => {
        if (isUncertainMatch(index)) next.add(index);
      });
      return next;
    });
  }

  // Flags a template column the agent mapped: "AI" for a confident match,
  // "Check" when it was unsure. Cleared once the user picks a field.
  function renderMatchBadge(index: number) {
    const meta = plannedColumns[index];
    if (
      !isTemplateSpreadsheet ||
      !meta?.target ||
      userMappedColumns.has(index) ||
      mapping[index] !== meta.target
    ) {
      return null;
    }
    const confidence = Math.round(meta.confidence * 100);
    if (isUncertainMatch(index)) {
      return (
        <span
          className='ih-badge ih-badge-warning'
          title={`Low-confidence match (${confidence}%)${
            meta.reason ? `: ${meta.reason}` : ''
          }. Please check it.`}
          style={{ flexShrink: 0 }}
        >
          Check
        </span>
      );
    }
    if (meta.source !== 'ai') return null;
    return (
      <span
        className='ih-badge ih-badge-info'
        title={`Matched by AI (${confidence}% confident)${
          meta.reason ? `: ${meta.reason}` : ''
        }`}
        style={{ flexShrink: 0, display: 'inline-flex', gap: 3 }}
      >
        <Sparkles size={11} /> AI
      </span>
    );
  }

  function onContinue() {
    if (currentStep === 'setup' && !name.trim()) {
      toast.warning('Give the database a name to continue.');
      return;
    }
    if (!groupId || !createdBy) {
      toast.error(
        'Missing team or user information — cannot create a database.',
      );
      return;
    }
    if (currentStep === 'upload' && !fileIsReady) {
      toast.warning(
        mappingNeedsAttention
          ? 'Review the required column matches before continuing.'
          : 'Upload a supported file before continuing.',
      );
      return;
    }
    if (currentStep === 'documents' && !documentsStepIsReady) {
      toast.warning(
        aiMappingRunning
          ? 'Still matching your columns — one moment.'
          : lowConfidenceCount > 0
          ? `Confirm or change the ${lowConfidenceCount} uncertain column ${
              lowConfidenceCount === 1 ? 'match' : 'matches'
            } before continuing.`
          : mappingNeedsAttention
          ? 'Review the required column matches before continuing.'
          : 'Attach your product spreadsheet or a customs entry PDF before continuing.',
      );
      return;
    }
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  }

  async function onSubmit() {
    if (!name.trim()) return;
    if (
      importsProducts &&
      fileFormat !== 'xml' &&
      importNameColIndices.length === 0
    ) {
      toast.warning(
        'Map a product name, description, or model number column before importing.',
      );
      return;
    }
    if (
      importsProducts &&
      fileFormat !== 'xml' &&
      pgaMaster &&
      htsCodeColIdx < 0
    ) {
      toast.warning('Map the Primary HTS column before importing.');
      return;
    }
    if (importsProducts && fileFormat !== 'xml' && importCount === 0) {
      toast.warning(
        pgaMaster
          ? 'The PGA Master template has no importable product rows.'
          : 'No importable product rows found — check that the product name column is mapped to a column that has values.',
      );
      return;
    }
    if (!groupId || !createdBy) {
      toast.error(
        'Missing team or user information — cannot create a database.',
      );
      return;
    }

    setSaving(true);
    setImportJob(null);
    const generation = pollGenerationRef.current + 1;
    pollGenerationRef.current = generation;
    // Set when a "Use your template" spreadsheet import finishes: the user
    // lands on the new database instead of staying on the hub list.
    let openDatabaseId: string | null = null;
    let normalizationNote: string | null = null;
    try {
      if (importsProducts) {
        if (!file) {
          toast.error('Choose a file to import.');
          return;
        }
        if (!fileFormat) {
          toast.error('Choose a supported CSV, XLSX, or XML file.');
          return;
        }

        const formData = new FormData();
        if (fileFormat === 'xml') {
          formData.append('file', file, file.name);
          formData.append('fileType', 'xml');
          formData.append('import_source', 'xml');
        } else {
          // "Use your template": rewrite the file into a clean CSV -- the
          // agent's sheet/header row, normalized dates, numbers, countries
          // and HTS codes -- so one odd cell can't fail an import batch.
          // Normalization follows the *final* mapping, so a column the user
          // re-mapped is cleaned up for the field they chose.
          let normalized: { file: File; stats: NormalizationStats } | null =
            null;
          if (isTemplateSpreadsheet && !pgaMaster) {
            const normalizeOptions = {
              fileType: fileFormat,
              headerRowIndex: templateHeaderRowIndex,
              headers,
              mapping,
              columns: plannedColumns,
            };
            normalized =
              fileFormat === 'csv'
                ? await rewriteCsvFile(file, normalizeOptions)
                : buildNormalizedCsvFile(
                    templateSheetRowsRef.current ?? [headers, ...rows],
                    file.name,
                    normalizeOptions,
                  );
            normalizationNote = describeNormalization(normalized.stats);
          }
          const rowCount = await getUploadRowCount(normalized?.file ?? file, {
            nameColIndex: importNameColIndices[0],
            fallbackNameColIndices: importNameColIndices.slice(1),
            countryColIndex: countryColIdx >= 0 ? countryColIdx : undefined,
          });
          if (rowCount === 0) {
            toast.warning(
              'No importable rows were found. Each product needs a name, description, or model number.',
            );
            return;
          }
          const check = await checkProductCredits(groupId, rowCount);
          if (!check.ok) {
            setCreditBlock(check);
            return;
          }

          const { resolvedHeaders, notMappedColumns } =
            buildProductImportMetadata(
              headers,
              mapping,
              pgaMaster ?? undefined,
            );
          const uploadFile = normalized
            ? normalized.file
            : fileFormat === 'csv'
            ? file
            : new File(
                [
                  buildMappedProductsCsv(
                    headers,
                    rows,
                    mapping,
                    nameColIdx,
                    pgaMaster ?? undefined,
                  ).csvBlob,
                ],
                'products.csv',
                { type: 'text/csv' },
              );

          formData.append('file', uploadFile, uploadFile.name);
          formData.append('fileType', 'csv');
          formData.append('import_source', 'csv_xlsx');
          formData.append('streamingImport', 'true');
          if (pgaMaster) {
            formData.append('pgaMasterTemplate', 'true');
            formData.append(
              'pgaBaseColumnCount',
              String(pgaMaster.baseColumnCount),
            );
          }
          formData.append('resolvedHeaders', JSON.stringify(resolvedHeaders));
          formData.append('notMappedColumns', JSON.stringify(notMappedColumns));
        }
        formData.append('name', name.trim());
        if (pendingImportDatabaseId) {
          formData.append('database_id', pendingImportDatabaseId);
        }
        formData.append('group_id', groupId);
        formData.append('created_by', createdBy);
        formData.append('icon', iconKey);
        formData.append('accent_color', accent);
        formData.append(
          'database_structure',
          isTemplateSpreadsheet ? templateStructure : databaseStructure,
        );
        formData.append('screening_frequency', screeningFrequency);
        if (screeningFrequency === 'Weekly') {
          formData.append('screening_day_of_week', screeningDayOfWeek);
        }
        formData.append('notifications_email', notificationChannels.join(','));
        formData.append(
          'notifications_enabled',
          String(notificationChannels.length > 0),
        );
        if (description.trim())
          formData.append('description', description.trim());
        const { success, error, jobId, databaseId } =
          await bulkUploadImportProducts(formData);
        if (!success) {
          toast.error(error ?? 'Failed to create database');
          return;
        }
        if (databaseId) setPendingImportDatabaseId(databaseId);

        if (jobId) {
          try {
            const job = await waitForImportProductJob(jobId, {
              onProgress: setImportJob,
              isCancelled: () => pollGenerationRef.current !== generation,
            });
            if (!job) return;
            if (job.status === 'completed') {
              if (isTemplateSpreadsheet && databaseId) {
                openDatabaseId = databaseId;
              }
              toast.success(
                `${job.insertedRows} product${
                  job.insertedRows === 1 ? '' : 's'
                } imported${
                  (job.pgaFlagsCreated ?? 0) > 0
                    ? ` with ${job.pgaFlagsCreated} PGA flag${
                        job.pgaFlagsCreated === 1 ? '' : 's'
                      }`
                    : ''
                }`,
              );
              if (job.warnings?.length) {
                toast.warning(
                  `${job.warnings.length} parent link${
                    job.warnings.length === 1 ? '' : 's'
                  } could not be created. ${job.warnings[0].message}${
                    job.warnings.length > 1 ? ' (and more)' : ''
                  }.`,
                );
              }
              if (normalizationNote) {
                toast.warning(normalizationNote);
              }
            } else {
              if (
                job.errorCode === 'PRODUCT_CAPACITY_EXCEEDED' &&
                job.creditCheck
              ) {
                if (job.databaseRemoved) {
                  setPendingImportDatabaseId(null);
                  onCreated?.();
                }
                setCreditBlock({ ok: false, ...job.creditCheck });
                return;
              }
              toast.error(job.error ?? 'Product import failed');
              return;
            }
          } catch (monitorError) {
            toast.error(
              monitorError instanceof Error
                ? monitorError.message
                : 'Failed to monitor product import',
            );
            return;
          }
        } else {
          toast.success('Database created');
        }
      } else {
        const { success, error, database } = await createImportDatabase({
          name: name.trim(),
          groupId,
          createdBy,
          description: description.trim() || undefined,
          icon: iconKey,
          accentColor: accent,
          // Only "Start empty" and a PDF hand-off reach this branch; neither
          // shows the Hierarchy / Flat cards, so they're created as a
          // hierarchy (which also works as a flat list).
          databaseStructure: 'hierarchy',
          screeningFrequency,
          screeningDayOfWeek:
            screeningFrequency === 'Weekly' ? screeningDayOfWeek : undefined,
          notificationChannels,
        });
        if (!success) {
          toast.error(error ?? 'Failed to create database');
          return;
        }
        toast.success('Database created');

        if (startMode === 'documents' && database && documentFile) {
          onCreatedForDocumentUpload?.(
            { id: database.id, name: database.name },
            documentFile,
          );
          resetAll();
          onOpenChange(false);
          return;
        }
      }

      onCreated?.();
      resetAll();
      onOpenChange(false);
      if (openDatabaseId) {
        allowNavigationRef.current = true;
        history.push(`/import-hub/db/${openDatabaseId}`);
        window.setTimeout(() => {
          allowNavigationRef.current = false;
        }, 0);
      }
    } finally {
      if (pollGenerationRef.current === generation) setSaving(false);
    }
  }

  return (
    <>
      <Prompt when={shouldWarnOnLeave} message={handleBlockedNavigation} />
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <div
          className={cx(
            'ih-create-db-dialog',
            reviewingColumns && 'is-reviewing',
          )}
          role='dialog'
          aria-modal='true'
          aria-label='Create Database'
        >
          {/* Header */}
          <div className='ih-create-db-header'>
            <h2 className='ih-create-db-title'>Create Database</h2>
            <button
              className='ih-create-db-close'
              type='button'
              onClick={() => handleOpenChange(false)}
              aria-label='Close'
            >
              <X size={20} />
            </button>
          </div>

          {/* Stepper */}
          <div className='ih-create-db-stepper'>
            {steps.map((s, idx) => {
              const state =
                idx < stepIdx
                  ? 'done'
                  : idx === stepIdx
                  ? 'active'
                  : 'upcoming';
              const dotBg =
                state === 'done'
                  ? 'var(--full-bar-color, #22a549)'
                  : state === 'active'
                  ? 'var(--action-color)'
                  : 'var(--bg-surface)';
              const dotColor =
                state === 'upcoming' ? 'var(--text-secondary)' : '#fff';
              return (
                <div className='ih-create-db-step-wrap' key={s}>
                  <div className={`ih-create-db-step is-${state}`}>
                    <div
                      className='ih-create-db-step-dot'
                      style={{
                        background: dotBg,
                        color: dotColor,
                      }}
                    >
                      {state === 'done' ? <Check size={14} /> : idx + 1}
                    </div>
                    <div className='ih-create-db-step-label'>
                      {STEP_LABEL[s]}
                    </div>
                  </div>
                  {idx < steps.length - 1 && (
                    <div
                      className='ih-create-db-step-line'
                      style={{
                        background:
                          idx < stepIdx
                            ? 'var(--full-bar-color, #22a549)'
                            : 'var(--es-soft-border, var(--border-color))',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Body */}
          <div className='ih-create-db-body'>
            {currentStep === 'setup' && (
              <>
                <div className='ih-create-db-field'>
                  <label
                    className='ih-create-db-label'
                    htmlFor='create-database-name'
                  >
                    Name <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <input
                    id='create-database-name'
                    autoFocus
                    maxLength={200}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder='e.g. Aerospace Components'
                    className='ih-create-db-input'
                  />
                </div>
                <div className='ih-create-db-field'>
                  <label
                    className='ih-create-db-label'
                    htmlFor='create-database-description'
                  >
                    Description
                  </label>
                  <textarea
                    id='create-database-description'
                    rows={2}
                    maxLength={2000}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder='What this database tracks'
                    className='ih-create-db-input ih-create-db-textarea'
                  />
                </div>
                <div className='ih-create-db-hint'>
                  Databases are shared with everyone on your team.
                </div>
                {(!groupId || !createdBy) && (
                  <div className='ih-edit-error' role='alert'>
                    Team or user information is unavailable. Database creation
                    is disabled until it is restored.
                  </div>
                )}
              </>
            )}

            {currentStep === 'setup' && (
              <>
                <div className='ih-create-db-field'>
                  <label className='ih-create-db-label'>
                    How do you want to start?
                  </label>
                  <div className='ih-create-db-source-grid ih-create-db-source-grid-3'>
                    {[
                      {
                        key: 'file' as const,
                        title: 'Use BITE template',
                        sub: 'Upload products from a BITE template (CSV, XLSX, or CargoWise XML).',
                      },
                      {
                        key: 'documents' as const,
                        title: 'Use your template',
                        sub: 'Upload your own product spreadsheet (CSV/XLSX) or a customs entry PDF — we’ll match it to BITE fields.',
                      },
                      {
                        key: 'manual' as const,
                        title: 'Start empty',
                        sub: 'Create the database, then add products by hand.',
                      },
                    ].map((opt) => {
                      const selected = startMode === opt.key;
                      return (
                        <button
                          className={cx(
                            'ih-create-db-source-card',
                            selected && 'is-active',
                          )}
                          key={opt.key}
                          type='button'
                          onClick={() => selectStartMode(opt.key)}
                          aria-pressed={selected}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            textAlign: 'left',
                            borderRadius: 8,
                            cursor: 'pointer',
                            background: 'var(--bg-surface)',
                            border: selected
                              ? '2px solid var(--action-color)'
                              : '1px solid var(--edge-soft)',
                            padding: selected ? 11 : 12,
                          }}
                        >
                          <div
                            className='ih-create-db-source-label'
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: 'var(--text-primary)',
                              fontFamily: FONT_ROBOTO,
                            }}
                          >
                            {opt.title}
                          </div>
                          <div
                            className='ih-create-db-source-subtitle'
                            style={{
                              fontSize: 11,
                              color: 'var(--text-tertiary)',
                              fontFamily: FONT_ROBOTO,
                            }}
                          >
                            {opt.sub}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Only "Import from file" asks for a structure: a template
                    upload detects it from the file, and an empty database or a
                    PDF hand-off is created as a hierarchy, which also covers
                    flat lists. */}
                {startMode === 'file' && (
                  <div className='ih-create-db-field'>
                    <label className='ih-create-db-label'>
                      Ways to organize products
                    </label>
                    <div
                      className='ih-create-db-store-grid'
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 10,
                      }}
                    >
                      {(
                        [
                          {
                            value: 'hierarchy',
                            title: 'Hierarchy (BOM)',
                            Icon: Workflow,
                            body: 'Build Assembly, Sub-Assembly, and Component relationships using parent SKUs.',
                          },
                          {
                            value: 'flat',
                            title: 'Flat list',
                            Icon: List,
                            body: 'Track every product independently without parent/child relationships.',
                          },
                        ] as const
                      ).map(({ value, title, Icon, body }) => {
                        const selected = databaseStructure === value;
                        return (
                          <button
                            key={value}
                            className={cx(
                              'ih-create-db-store-card',
                              selected && 'is-active',
                            )}
                            type='button'
                            aria-pressed={selected}
                            onClick={() => selectDatabaseStructure(value)}
                            style={{
                              padding: 14,
                              borderRadius: 10,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8,
                              border: selected
                                ? '2px solid var(--action-color)'
                                : '1px solid var(--edge-soft)',
                              background: 'var(--bg-surface)',
                              color: 'inherit',
                              textAlign: 'left',
                              cursor: 'pointer',
                            }}
                          >
                            <div
                              className='ih-create-db-store-title'
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              <Icon size={16} /> {title}
                            </div>
                            {value === 'hierarchy' && (
                              <div
                                className='ih-create-db-store-body'
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 4,
                                }}
                              >
                                <span>Assembly</span> →{' '}
                                <span>Sub-Assembly</span> →{' '}
                                <span>Component</span>
                              </div>
                            )}
                            <div className='ih-create-db-store-body'>
                              {body}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {currentStep === 'setup' && (
              <button
                className='ih-create-db-secondary-button'
                type='button'
                onClick={() => setAppearanceOpen((current) => !current)}
                aria-expanded={appearanceOpen}
              >
                {appearanceOpen
                  ? 'Hide appearance options'
                  : 'Customize appearance (optional)'}
              </button>
            )}

            {currentStep === 'setup' && appearanceOpen && (
              <>
                <div className='ih-create-db-field'>
                  <label className='ih-create-db-label'>Icon</label>
                  <div className='ih-create-db-icon-grid'>
                    {ICON_CHOICES.map(({ key, Icon }) => {
                      const selected = iconKey === key;
                      return (
                        <button
                          key={key}
                          className={cx(
                            'ih-create-db-icon-button',
                            selected && 'is-active',
                          )}
                          type='button'
                          onClick={() => setIconKey(key)}
                          aria-label={key}
                          style={{
                            border: selected
                              ? `2px solid ${accent}`
                              : '1px solid var(--edge-soft)',
                            color: selected ? accent : 'var(--text-secondary)',
                          }}
                        >
                          <Icon size={18} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className='ih-create-db-field'>
                  <label className='ih-create-db-label'>Accent color</label>
                  <div className='ih-create-db-swatch-row'>
                    {ACCENTS.map((c) => {
                      const selected = accent === c;
                      return (
                        <button
                          key={c}
                          className={cx(
                            'ih-create-db-swatch',
                            selected && 'is-active',
                          )}
                          type='button'
                          onClick={() => setAccent(c)}
                          aria-label={c}
                          style={{
                            background: c,
                            border: `2px solid ${
                              selected ? 'var(--bg-colors)' : 'transparent'
                            }`,
                            boxShadow: selected
                              ? '0 0 0 2px var(--text-primary)'
                              : '0 0 0 1px var(--edge-soft)',
                            cursor: 'pointer',
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className='ih-create-db-field'>
                  <label className='ih-create-db-label'>Preview</label>
                  <div className='ih-create-db-preview'>
                    <div
                      className='ih-create-db-preview-icon'
                      style={{
                        background: accent + '22',
                        color: accent,
                      }}
                    >
                      <SelectedIcon size={22} />
                    </div>
                    <div
                      className='ih-create-db-preview-name'
                      style={{
                        color: name
                          ? 'var(--text-primary)'
                          : 'var(--text-tertiary)',
                      }}
                    >
                      {name || 'Database name'}
                    </div>
                  </div>
                </div>
              </>
            )}

            {currentStep === 'upload' && (
              <>
                {!mappingOpen && !mappingNeedsAttention && (
                  <div className='ih-create-db-field' style={{ order: 2 }}>
                    <div
                      className='ih-create-db-label'
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'var(--text-secondary)',
                        fontFamily: FONT_ROBOTO,
                        marginBottom: 6,
                      }}
                    >
                      Accepted file templates
                    </div>
                    <div
                      className='ih-create-db-hint'
                      style={{
                        fontSize: 12,
                        color: 'var(--text-tertiary)',
                        fontFamily: FONT_ROBOTO,
                        marginBottom: 8,
                      }}
                    >
                      Download an example to see the CSV columns or CargoWise
                      XML structure that BITE accepts.
                      {databaseStructure === 'hierarchy' && (
                        <>
                          {' '}
                          In the BOM template, each row&apos;s{' '}
                          <strong>Parent</strong> must match another row&apos;s{' '}
                          <strong>SKU</strong> (model number). Quantity and unit
                          of measure on child rows describe how many go into the
                          parent.
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                      }}
                    >
                      <button
                        className='ih-create-db-secondary-button'
                        type='button'
                        onClick={() => downloadTemplate(databaseStructure)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          width: '100%',
                          borderRadius: 6,
                          padding: '10px 16px',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: 'var(--bg-colors)',
                          border: '1px solid var(--edge-soft)',
                          color: 'var(--text-primary)',
                          fontFamily: FONT_INTER,
                        }}
                      >
                        <Download size={18} />{' '}
                        {databaseStructure === 'hierarchy'
                          ? 'Download BOM Template'
                          : 'Download CSV Template'}
                      </button>
                      <button
                        className='ih-create-db-secondary-button'
                        type='button'
                        onClick={downloadCargoWiseXmlExample}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          width: '100%',
                          borderRadius: 6,
                          padding: '10px 16px',
                          fontSize: 14,
                          fontWeight: 600,
                          cursor: 'pointer',
                          background: 'var(--bg-colors)',
                          border: '1px solid var(--edge-soft)',
                          color: 'var(--text-primary)',
                          fontFamily: FONT_INTER,
                        }}
                      >
                        <Download size={18} /> Download CargoWise XML Example
                      </button>
                    </div>
                  </div>
                )}

                <div className='ih-create-db-field' style={{ order: 1 }}>
                  <div className='ih-create-db-label'>
                    Supported import paths
                  </div>
                  <div className='ih-create-db-hint'>
                    Upload a BITE default product catalog file, CargoWise
                    Product XML, or a Detailed Product with PGA file. BITE will
                    detect the format automatically.
                  </div>
                  <div
                    className='ih-create-db-label'
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--text-secondary)',
                      fontFamily: FONT_ROBOTO,
                      marginBottom: 6,
                    }}
                  >
                    Upload your file
                  </div>
                  <div
                    className={cx(
                      'ih-create-db-dropzone',
                      dragActive && 'is-active',
                      rejectType && 'is-rejected',
                      file && 'has-file',
                    )}
                    role='button'
                    tabIndex={0}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ')
                        fileInputRef.current?.click();
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragActive(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f) void acceptFile(f);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 10,
                      cursor: 'pointer',
                      padding:
                        mappingOpen || mappingNeedsAttention
                          ? '10px 16px'
                          : '28px 16px',
                      background: dragActive
                        ? 'var(--bg-hover)'
                        : 'var(--bg-surface)',
                      border: file
                        ? '1.5px solid var(--full-bar-color, #22a549)'
                        : rejectType
                        ? '1.5px dashed var(--danger)'
                        : dragActive
                        ? '1.5px dashed var(--action-color)'
                        : '1.5px dashed var(--edge-soft)',
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type='file'
                      accept='.csv,.xlsx,.xml,application/xml,text/xml'
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void acceptFile(f);
                        e.currentTarget.value = '';
                      }}
                    />
                    {fileParsing ? (
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--text-secondary)',
                          fontFamily: FONT_INTER,
                        }}
                        role='status'
                      >
                        Reading file…
                      </div>
                    ) : file ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 4,
                          textAlign: 'center',
                        }}
                      >
                        {!mappingOpen && !mappingNeedsAttention && (
                          <Check
                            size={22}
                            style={{ color: 'var(--full-bar-color, #22a549)' }}
                          />
                        )}
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            fontFamily: FONT_INTER,
                          }}
                        >
                          {file.name}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-tertiary)',
                            fontFamily: FONT_ROBOTO,
                          }}
                        >
                          {fileFormat === 'xml'
                            ? `XML · ${formatFileSize(
                                file.size,
                              )} · validated during import`
                            : `${
                                fileFormat === 'csv' && rows.length >= 100
                                  ? `Previewing first ${rows.length} rows`
                                  : `${rows.length} ${
                                      rows.length === 1 ? 'row' : 'rows'
                                    } detected`
                              } · click to replace`}
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 4,
                          textAlign: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13,
                            color: 'var(--text-secondary)',
                            fontFamily: FONT_ROBOTO,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              color: 'var(--action-color)',
                            }}
                          >
                            Click to upload
                          </span>{' '}
                          or drag & drop
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-tertiary)',
                            fontFamily: FONT_ROBOTO,
                          }}
                        >
                          CSV, XLSX, or XML
                        </div>
                        {rejectType && (
                          <div
                            style={{
                              fontSize: 11,
                              color: 'var(--danger)',
                              fontFamily: FONT_ROBOTO,
                            }}
                          >
                            Unsupported file type.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {fileFormat === 'xml' && (
                    <div
                      className='ih-create-db-hint'
                      style={{
                        fontSize: 12,
                        color: 'var(--text-tertiary)',
                        fontFamily: FONT_ROBOTO,
                        lineHeight: 1.5,
                        marginTop: 8,
                      }}
                    >
                      Product mapping, PGA extraction, validation, and credit
                      checks will run during the secure import.
                    </div>
                  )}
                </div>
              </>
            )}

            {showsColumnMapping &&
              !aiMappingRunning &&
              mappingNeedsAttention && (
                <div
                  className='ih-edit-error'
                  role='alert'
                  style={{ order: 3 }}
                >
                  Match at least one Product name, Description, or Model number
                  column
                  {pgaMaster && htsCodeColIdx < 0
                    ? ', plus the Primary HTS column'
                    : ''}
                  , before importing.
                </div>
              )}

            {showsColumnMapping &&
              !aiMappingRunning &&
              fallbackNamePreviewCount > 0 && (
                <div
                  className='ih-callout-warning ih-row ih-gap-2 ih-text-xs'
                  role='status'
                  style={{ order: 3 }}
                >
                  <AlertCircle size={16} className='ih-tone-warning' />
                  <span>
                    {fallbackNamePreviewCount} preview{' '}
                    {fallbackNamePreviewCount === 1 ? 'row is' : 'rows are'}{' '}
                    missing a Product Name. Import Hub will use Description
                    first, then Model Number, for those products.
                  </span>
                </div>
              )}

            {showsColumnMapping &&
              !aiMappingRunning &&
              file &&
              fileFormat !== 'xml' &&
              (rows.length === 0 ||
                (!mappingNeedsAttention && importCount === 0)) && (
                <div
                  className='ih-edit-error'
                  role='alert'
                  style={{ order: 3 }}
                >
                  {rows.length === 0
                    ? 'No product rows were found in this file.'
                    : pgaMaster
                    ? 'The PGA Master file has no importable rows with both a product identity and Primary HTS value.'
                    : 'No rows have a value in the mapped product name column, so this file would import zero products. Check the column mapping below.'}
                </div>
              )}

            {showsColumnMapping &&
              !aiMappingRunning &&
              file &&
              fileFormat !== 'xml' && (
                <button
                  className='ih-create-db-secondary-button'
                  type='button'
                  onClick={() =>
                    setMappingOpen((current) =>
                      mappingNeedsAttention ? true : !current,
                    )
                  }
                  aria-expanded={mappingOpen || mappingNeedsAttention}
                  style={{ order: 4 }}
                >
                  {mappingNeedsAttention
                    ? 'Review required column matches'
                    : mappingOpen
                    ? 'Done reviewing column matches'
                    : isTemplateSpreadsheet && aiMappingStatus === 'done'
                    ? `Columns mapped automatically (${mappedCount}) — Review${
                        lowConfidenceCount > 0
                          ? ` · ${lowConfidenceCount} to check`
                          : ''
                      }`
                    : `Columns matched (${mappedCount}) — Review`}
                </button>
              )}

            {showsColumnMapping &&
              isTemplateSpreadsheet &&
              !aiMappingRunning &&
              lowConfidenceCount > 0 && (
                <div
                  className='ih-callout-warning ih-row ih-gap-2 ih-text-xs'
                  role='status'
                  style={{ order: 3, alignItems: 'center' }}
                >
                  <AlertCircle size={16} className='ih-tone-warning' />
                  <span style={{ flex: 1 }}>
                    {lowConfidenceCount} column{' '}
                    {lowConfidenceCount === 1 ? 'match needs' : 'matches need'}{' '}
                    your confirmation before you can continue. Confirm each one,
                    or pick a different field.
                  </span>
                  {!mappingOpen ? (
                    <button
                      type='button'
                      className='ih-create-db-secondary-button'
                      onClick={() => {
                        setMappingOpen(true);
                        setMappingFilter('check');
                      }}
                    >
                      Review
                    </button>
                  ) : (
                    <button
                      type='button'
                      className='ih-create-db-secondary-button'
                      onClick={confirmAllUncertainMatches}
                    >
                      Confirm all {lowConfidenceCount}
                    </button>
                  )}
                </div>
              )}

            {showsColumnMapping &&
              isTemplateSpreadsheet &&
              !aiMappingRunning &&
              mappingWarnings.length > 0 && (
                <div
                  className='ih-callout-warning ih-row ih-gap-2 ih-text-xs'
                  role='status'
                  style={{ order: 3 }}
                >
                  <AlertCircle size={16} className='ih-tone-warning' />
                  <span>{mappingWarnings.join(' ')}</span>
                </div>
              )}

            {reviewingColumns && (
              <>
                <div
                  className='ih-create-db-hint'
                  style={{
                    order: 5,
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    fontFamily: FONT_ROBOTO,
                    lineHeight: 1.6,
                  }}
                >
                  {pgaMaster ? (
                    <>
                      PGA Master template detected. Base Commodity fields are
                      mapped below; its{' '}
                      <strong>{pgaMaster.pgaColumnCount}</strong> PGA columns
                      across <strong>{pgaMaster.pgaSectionCount}</strong>{' '}
                      supported sections will be imported automatically.{' '}
                      <strong>{importCount}</strong>{' '}
                      {importCount === 1 ? 'product row' : 'product rows'} and{' '}
                      <strong>{pgaMaster.populatedPgaRecords}</strong> populated
                      PGA
                      {pgaMaster.populatedPgaRecords === 1
                        ? ' record'
                        : ' records'}{' '}
                      detected.
                    </>
                  ) : isTemplateSpreadsheet ? (
                    <>
                      BITE matched your columns to product fields automatically.
                      Change any match below, or keep a column as an additional
                      column. <strong>{mappedCount}</strong> of {headers.length}{' '}
                      columns mapped · <strong>{importCount}</strong>{' '}
                      {importCount === 1
                        ? 'preview row is'
                        : 'preview rows are'}{' '}
                      ready to import. Dates, numbers, countries and HTS codes
                      are standardized when you import.{' '}
                      {templateStructure === 'hierarchy'
                        ? 'Parent or hierarchy columns were found, so this database will be a BOM hierarchy.'
                        : 'Map a Parent column to build a BOM hierarchy; otherwise this database is a flat list.'}
                    </>
                  ) : (
                    <>
                      Exact matches are auto-selected. Map each column from your
                      file to a product field, or keep it as an additional
                      column. <strong>{mappedCount}</strong> of {headers.length}{' '}
                      columns mapped · <strong>{importCount}</strong>{' '}
                      {importCount === 1
                        ? 'preview row is'
                        : 'preview rows are'}{' '}
                      ready for import validation.
                    </>
                  )}
                </div>
                <div className='ih-create-db-map-toolbar' style={{ order: 6 }}>
                  <div className='ih-create-db-map-filters'>
                    {(
                      [
                        ['all', 'All'],
                        ['mapped', 'Mapped'],
                        ['check', 'Needs check'],
                        ['additional', 'Additional'],
                      ] as const
                    ).map(([key, label]) =>
                      key === 'check' &&
                      mappingFilterCounts.check === 0 &&
                      mappingFilter !== 'check' ? null : (
                        <button
                          key={key}
                          type='button'
                          className={cx(
                            'ih-create-db-map-chip',
                            mappingFilter === key && 'is-active',
                          )}
                          aria-pressed={mappingFilter === key}
                          onClick={() => setMappingFilter(key)}
                        >
                          {label}
                          <span className='ih-create-db-map-chip-count'>
                            {mappingFilterCounts[key]}
                          </span>
                        </button>
                      ),
                    )}
                    <input
                      type='search'
                      className='ih-create-db-map-search'
                      placeholder='Search columns or values'
                      aria-label='Search file columns'
                      value={mappingSearch}
                      onChange={(e) => setMappingSearch(e.target.value)}
                    />
                  </div>
                  <div className='ih-create-db-map-headings' aria-hidden='true'>
                    <span style={{ flex: '0 0 38%' }}>File column</span>
                    <span className='ih-create-db-map-arrow'>→</span>
                    <span style={{ flex: 1 }}>BITE product field</span>
                  </div>
                </div>
                <div
                  style={{
                    order: 7,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {visibleMappingIndices.length === 0 && (
                    <div className='ih-create-db-map-empty'>
                      No columns match
                      {mappingSearchText ? ` "${mappingSearch.trim()}"` : ''} in
                      this view.
                    </div>
                  )}
                  {visibleMappingIndices.map((i) => {
                    const h = headers[i];
                    const used = new Set(
                      Object.entries(mapping)
                        .filter(([k]) => Number(k) !== i)
                        .map(([, v]) => v)
                        .filter(Boolean),
                    );
                    return (
                      <div
                        className='ih-create-db-map-row'
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <div
                          className='ih-create-db-map-source'
                          title={h}
                          style={{
                            flex: '0 0 38%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            minWidth: 0,
                            fontFamily: FONT_INTER,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {h || (
                                <span style={{ color: 'var(--text-tertiary)' }}>
                                  (unnamed)
                                </span>
                              )}
                            </span>
                            {columnSamples[i] && (
                              <span
                                className='ih-create-db-map-sample'
                                title={columnSamples[i]}
                              >
                                e.g. {columnSamples[i]}
                              </span>
                            )}
                          </span>
                          {renderMatchBadge(i)}
                        </div>
                        <span
                          className='ih-create-db-map-arrow'
                          style={{
                            color: 'var(--text-tertiary)',
                            fontSize: 12,
                          }}
                        >
                          →
                        </span>
                        <select
                          className='ih-create-db-input ih-create-db-map-select'
                          aria-label={`Map ${
                            h || 'unnamed column'
                          } to a BITE product field`}
                          value={mapping[i] ?? ''}
                          onChange={(e) =>
                            setColumnMapping(
                              i,
                              e.target.value as TemplateColumn | '',
                            )
                          }
                          style={{ ...inputStyle, flex: 1 }}
                        >
                          <option value=''>Keep as additional column</option>
                          {STRUCTURE_TEMPLATE_COLUMNS[mappingStructure].map(
                            (c) => (
                              <option
                                key={c}
                                value={c}
                                disabled={used.has(c) && mapping[i] !== c}
                              >
                                {formatTemplateColumn(c)}
                              </option>
                            ),
                          )}
                        </select>
                        {isUncertainMatch(i) && (
                          <button
                            type='button'
                            className='ih-create-db-secondary-button ih-create-db-map-confirm'
                            onClick={() => confirmColumnMatch(i)}
                            aria-label={`Confirm ${
                              h || 'unnamed column'
                            } as ${formatTemplateColumn(
                              mapping[i] as TemplateColumn,
                            )}`}
                          >
                            Confirm
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {currentStep === 'documents' && (
              <div className='ih-col ih-gap-3 ih-mt-3'>
                <div
                  role='button'
                  tabIndex={0}
                  onClick={() => documentFileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ')
                      documentFileInputRef.current?.click();
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDocumentDragActive(true);
                  }}
                  onDragLeave={() => setDocumentDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDocumentDragActive(false);
                    const dropped = e.dataTransfer.files?.[0];
                    if (dropped) handleTemplateFile(dropped);
                  }}
                  className={cx(
                    'ih-dropzone',
                    documentDragActive && 'is-active',
                    reviewingColumns && 'is-compact',
                  )}
                >
                  {reviewingColumns && file ? (
                    <div className='ih-create-db-file-chip'>
                      <FileStack size={16} className='ih-text-muted' />
                      <span className='ih-font-medium ih-text-sm'>
                        {file.name}
                      </span>
                      <span className='ih-text-xs ih-text-muted'>
                        {rows.length}{' '}
                        {fileFormat === 'csv' && rows.length >= 100
                          ? 'preview rows'
                          : rows.length === 1
                          ? 'row'
                          : 'rows'}{' '}
                        · click to replace
                      </span>
                    </div>
                  ) : aiMappingRunning ? (
                    <ColumnMatchingStatus fileName={file?.name} />
                  ) : fileParsing ? (
                    <div className='ih-map-working'>
                      <span className='ih-map-working-icon' aria-hidden='true'>
                        <Sparkles size={28} />
                      </span>
                      <div className='ih-font-medium ih-text-sm' role='status'>
                        Reading file…
                      </div>
                    </div>
                  ) : documentFile || file ? (
                    <>
                      <FileStack size={32} className='ih-text-muted' />
                      <div className='ih-mt-2 ih-font-medium ih-text-sm'>
                        {(documentFile ?? file)?.name}
                      </div>
                      {file && (
                        <div className='ih-text-xs ih-text-muted'>
                          {rows.length}{' '}
                          {fileFormat === 'csv' && rows.length >= 100
                            ? 'preview rows'
                            : rows.length === 1
                            ? 'row'
                            : 'rows'}{' '}
                          · {formatFileSize(file.size)}
                        </div>
                      )}
                      <button
                        type='button'
                        className='ih-create-db-secondary-button'
                        style={{ marginTop: 8 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (file) clearSelectedFile();
                          setDocumentFile(null);
                        }}
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <>
                      <Upload size={32} className='ih-text-muted' />
                      <div className='ih-mt-2 ih-font-medium ih-text-sm'>
                        Click to attach your product spreadsheet (CSV, XLSX) or
                        a customs entry PDF
                      </div>
                      <div className='ih-text-xs ih-text-muted'>
                        or drag and drop
                      </div>
                      {rejectType && (
                        <div
                          className='ih-text-xs'
                          style={{ color: 'var(--danger)' }}
                        >
                          Unsupported file type.
                        </div>
                      )}
                    </>
                  )}
                  <input
                    ref={documentFileInputRef}
                    type='file'
                    accept='.csv,.xlsx,.pdf,application/pdf'
                    className='ih-hidden'
                    onChange={(e) => {
                      const selected = e.target.files?.[0];
                      if (selected) handleTemplateFile(selected);
                      e.currentTarget.value = '';
                    }}
                  />
                </div>
                <p className='ih-text-xs ih-text-muted'>
                  {documentFile
                    ? "You'll pick how the PDF is analyzed (package check, entry parsing, or full comparison) on the next page, right after this database is created."
                    : file
                    ? "Columns are matched to BITE fields automatically — review them below. Columns you don't map are kept as additional columns."
                    : 'Spreadsheets are matched to BITE product fields automatically and imported into this database. A customs entry PDF opens in Document Upload once the database is created.'}
                </p>
              </div>
            )}

            {currentStep === 'screening' && (
              <>
                <p className='ih-create-db-settings-intro'>
                  Recommended defaults are already selected. You can change them
                  later from the database Settings tab.
                </p>

                <div className='ih-create-db-settings-fields'>
                  <div className='ih-create-db-settings-grid'>
                    <label className='ih-create-db-setting-field'>
                      <span className='ih-create-db-setting-label'>
                        <Clock size={18} /> Screening frequency
                      </span>
                      <span className='ih-create-db-select-wrap'>
                        <select
                          className='ih-create-db-setting-select'
                          value={screeningFrequency}
                          onChange={(event) =>
                            setScreeningFrequency(event.target.value)
                          }
                        >
                          {SCREENING_FREQUENCIES.map((frequency) => (
                            <option key={frequency} value={frequency}>
                              {frequency}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={20} />
                      </span>
                    </label>
                    {screeningFrequency === 'Weekly' && (
                      <label className='ih-create-db-setting-field'>
                        <span className='ih-create-db-setting-label'>
                          <Clock size={18} /> Day of week
                        </span>
                        <span className='ih-create-db-select-wrap'>
                          <select
                            className='ih-create-db-setting-select'
                            value={screeningDayOfWeek}
                            onChange={(event) =>
                              setScreeningDayOfWeek(event.target.value)
                            }
                          >
                            {WEEKDAYS.map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </select>
                          <ChevronDown size={20} />
                        </span>
                      </label>
                    )}
                  </div>

                  <div className='ih-create-db-setting-field'>
                    <span className='ih-create-db-field-label'>
                      Notify me via
                    </span>
                    <div className='ih-create-db-notification-cards'>
                      {DATABASE_NOTIFICATION_CHANNELS.map((channel) => {
                        const checked = notificationChannels.includes(channel);
                        return (
                          <label
                            key={channel}
                            className={`ih-create-db-notification-card${
                              checked ? ' is-active' : ''
                            }`}
                          >
                            <input
                              type='checkbox'
                              checked={checked}
                              onChange={(event) =>
                                setNotificationChannels((current) =>
                                  event.target.checked
                                    ? current.includes(channel)
                                      ? current
                                      : [...current, channel]
                                    : current.filter(
                                        (item) => item !== channel,
                                      ),
                                )
                              }
                            />
                            <span className='ih-create-db-notification-copy'>
                              <strong>{channel}</strong>
                              <small>
                                {DATABASE_NOTIFICATION_DESCRIPTIONS[channel]}
                              </small>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className='ih-create-db-footer'>
            <button
              className='ih-create-db-secondary-button'
              type='button'
              onClick={() => {
                if (stepIdx === 0) handleOpenChange(false);
                else setStepIdx((i) => Math.max(0, i - 1));
              }}
              disabled={saving}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                borderRadius: 6,
                padding: '10px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                background: 'var(--bg-colors)',
                border: '1px solid var(--edge-soft)',
                color: 'var(--text-primary)',
                fontFamily: FONT_INTER,
                opacity: saving ? 0.6 : 1,
              }}
            >
              {stepIdx === 0 ? 'Cancel' : 'Back'}
            </button>
            <div className='ih-create-db-step-count'>
              {saving
                ? savingStatusText
                : `Step ${stepIdx + 1} of ${steps.length}`}
            </div>
            {stepIdx < steps.length - 1 ? (
              <button
                className='ih-create-db-primary-button'
                type='button'
                onClick={onContinue}
                disabled={!currentStepIsReady}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  borderRadius: 6,
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  cursor: currentStepIsReady ? 'pointer' : 'not-allowed',
                  background: 'var(--action-color)',
                  border: '1px solid var(--action-color)',
                  fontFamily: FONT_INTER,
                }}
              >
                {currentStep === 'setup' && startMode === 'file'
                  ? 'Continue to upload'
                  : currentStep === 'setup' && startMode === 'documents'
                  ? 'Continue to upload'
                  : 'Continue to screening'}
              </button>
            ) : (
              <button
                className='ih-create-db-primary-button'
                type='button'
                onClick={onSubmit}
                disabled={!canSubmit}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  borderRadius: 6,
                  padding: '10px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  background: 'var(--action-color)',
                  border: '1px solid var(--action-color)',
                  fontFamily: FONT_INTER,
                  opacity: canSubmit ? 1 : 0.6,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {saving
                  ? 'Creating…'
                  : importsProducts
                  ? 'Create & Import'
                  : 'Create Database'}
              </button>
            )}
          </div>
        </div>
      </Dialog>
      <Dialog
        open={discardConfirmOpen}
        onOpenChange={(nextOpen) => {
          if (nextOpen) setDiscardConfirmOpen(true);
          else keepEditing();
        }}
      >
        <div
          className='ih-pga-confirm-card import-hub'
          role='alertdialog'
          aria-modal='true'
          aria-labelledby='discard-database-setup-title'
          aria-describedby='discard-database-setup-description'
        >
          <h4
            id='discard-database-setup-title'
            className='ih-font-semibold ih-text-sm'
          >
            Discard database setup?
          </h4>
          <p
            id='discard-database-setup-description'
            className='ih-text-xs ih-text-muted ih-mt-2'
          >
            Your database setup and any selected file will be lost.
          </p>
          <div
            className='ih-row ih-gap-2 ih-mt-3'
            style={{ justifyContent: 'flex-end' }}
          >
            <button
              type='button'
              className='ih-btn ih-btn-outline ih-btn-sm'
              onClick={keepEditing}
              autoFocus
            >
              Keep editing
            </button>
            <button
              type='button'
              className='ih-btn ih-btn-danger ih-btn-sm'
              onClick={discardAndClose}
            >
              Discard
            </button>
          </div>
        </div>
      </Dialog>
      <ProductCreditsModal
        check={creditBlock}
        teamId={groupId ?? ''}
        onClose={() => setCreditBlock(null)}
      />
    </>
  );
};
